import { BrandMark } from '@/core/AppShell/BrandMark';

import type { ReactElement, ReactNode } from 'react';

export interface OutsideShellHeaderProps {
  /**
   * Optional trailing content pinned to the inline-end of the bar (e.g. a
   * "Sign in" link). Omit for a bare branded bar.
   */
  readonly end?: ReactNode;
}

/**
 * Shared brand bar for the pages that render OUTSIDE the authenticated AppShell
 * (sign-in, verify-email, onboarding, CLI-auth, invite-accept, checkout). Those
 * pages otherwise show no Syncanix identity at all, which reads as an unbranded /
 * untrusted surface right where trust matters most (auth + payment).
 *
 * The brand cluster is the app shell's own `BrandMark` — glyph plus wordmark —
 * rather than a bare text wordmark. Every one of these pages previously drew
 * `<span>Syncanix</span>` and no logo at all, so the surfaces that need to look
 * most trustworthy were the only ones showing no mark. Sharing the component
 * also means the wordmark's size and weight are decided in exactly one place;
 * five hand-assembled copies of the same size-plus-weight pair were five
 * chances to drift.
 *
 * `/scan`, `/oauth/consent`, `/r/:token` and the unsubscribe page carried
 * byte-identical hand-rolled copies of this bar and now render this one, so
 * every outside-shell surface reads as one product. Spacing is logical (flex
 * `gap` / `justify-between`), so it is RTL-correct with no physical directional
 * properties. Presentational + i18n-free (the wordmark is the brand name,
 * identical in every locale).
 */
export function OutsideShellHeader({ end }: OutsideShellHeaderProps): ReactElement {
  return (
    <header className="flex items-center justify-between border-b border-border px-6 py-4">
      <BrandMark wordmark="always" />
      {end ?? null}
    </header>
  );
}
